import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// v3.14.0 (UI 打磨): 会话清除事件 — 模块级 EventTarget (store 实例可能被
// 测试重复创建, 事件源需与其一致且全局唯一)。
const sessionClearTarget = new EventTarget()

/** 订阅会话清除事件 (登出/401); 返回解绑函数。 */
export function onSessionCleared(cb: () => void): () => void {
  const handler = (): void => cb()
  sessionClearTarget.addEventListener('clear', handler)
  return () => sessionClearTarget.removeEventListener('clear', handler)
}

function emitSessionCleared(): void {
  sessionClearTarget.dispatchEvent(new Event('clear'))
}

export interface AuthUser {
  username: string
  role?: string
  expiresAt?: number
  // v3.13.92: 昵称 (展示名) 与头像版本戳 (0 = 无头像)。
  nickname?: string
  is_default?: boolean
  avatarV?: number
}

const STORAGE_KEY = 'afd.auth'
const TOKEN_KEY = 'afd.auth.token'

interface PersistedAuth {
  user: AuthUser | null
  token: string | null
}

function readPersisted(): PersistedAuth {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { user: null, token: null }
    const parsed = JSON.parse(raw) as PersistedAuth
    return {
      user: parsed.user ?? null,
      token: parsed.token ?? null,
    }
  } catch {
    return { user: null, token: null }
  }
}

function writePersisted(value: PersistedAuth): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    if (value.token) {
      sessionStorage.setItem(TOKEN_KEY, value.token)
    } else {
      sessionStorage.removeItem(TOKEN_KEY)
    }
  } catch {
    /* storage unavailable */
  }
}

function clearPersisted(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
    sessionStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const token = ref<string | null>(null)
  const initialized = ref(false)
  // 管理员账号是否已初始化 (null = 尚未查询)。初始化向导路由守卫用:
  // 未初始化 → 强制进 /setup; 已初始化 → 禁止再进 /setup。
  const accountConfigured = ref<boolean | null>(null)

  const isAuthenticated = computed(() => !!token.value && !!user.value)

  function initFromStorage(): void {
    if (initialized.value) return
    const persisted = readPersisted()
    user.value = persisted.user
    token.value = persisted.token
    initialized.value = true
  }

  // v3.13.92 fix: 启动时用 cookie 会话恢复 auth.user — 此前 checkAuth()
  // 导出了却从未调用, localStorage 里的 user 一旦被 401 兜底清掉
  // (后端重启窗口 / 网络瞬断), 头像和用户名就永久消失, 只剩 token
  // 撑着 isAuthenticated。cookie 有效则完整恢复 user + token。
  async function restoreSession(): Promise<void> {
    try {
      const { checkAuth } = await import('@/api/auth')
      const st = await checkAuth()
      if (st?.user && st?.token) {
        setAuth(st.user, st.token)
      }
    } catch {
      /* cookie 失效: 保持本地状态, 路由守卫按未登录处理 */
    }
  }

  async function ensureAccountConfigured(): Promise<boolean> {
    if (accountConfigured.value !== null) return accountConfigured.value
    try {
      const { getAuthStatus } = await import('@/api/auth')
      const st = await getAuthStatus()
      accountConfigured.value = st.configured
    } catch {
      // 查询失败: 默认按"未初始化"处理, 让守卫跳到 /setup。
      // Round 10.1 fix: 之前的兜底 (设成 true) 会导致节点重启/网络瞬断时,
      // SPA 误认为已配置, 把用户弹到登录页, 实际后端还没初始化 → 死锁
      // (登录页说 "账号未初始化", 但向导路由永远进不来)。
      // 后端在没初始化时本来就不需要认证, /setup 是 public 端点。
      accountConfigured.value = false
    }
    return accountConfigured.value ?? false
  }

  function setAccountConfigured(v: boolean): void {
    accountConfigured.value = v
  }

  function setAuth(nextUser: AuthUser, nextToken: string): void {
    user.value = nextUser
    token.value = nextToken
    initialized.value = true
    writePersisted({ user: nextUser, token: nextToken })
  }

  // v3.14.0 (UI 打磨): 会话清除事件 — 登出/401 后由长生命周期资源
  // (WebSocket / 轮询定时器) 监听并主动断开。此前登出只清本地凭证:
  // WS 仍持旧会话无限重连 (服务端 401 → 1.5s 重试循环), 仪表盘 1s
  // 轮询也继续打未授权请求。

  function clearAuth(): void {
    user.value = null
    token.value = null
    initialized.value = true
    clearPersisted()
    emitSessionCleared()
  }

  function getToken(): string | null {
    return token.value
  }

  return {
    user,
    token,
    initialized,
    accountConfigured,
    isAuthenticated,
    initFromStorage,
    ensureAccountConfigured,
    restoreSession,
    setAccountConfigured,
    setAuth,
    clearAuth,
    getToken,
  }
})
